import { FilterType, Button, Icons } from '@120wateraudit/envirio-components'

import { GQLGrid } from '../../containers/GQLGrid'
import { FACILITY_GRID_QUERY } from '../../data-access/facility-grid-query'
import { client } from '../../utils/GQLClient'
import { downloadFacilityDetailsExportExcel } from '../../utils/RESTClient'
import { Responsive } from '../Responsive/Responsive'
import { GridMemo } from '../GridMemo/GridMemo'
import { AccordionRow } from './AccordionRow/AccordionRow'

import styles from './FacilityGrid.module.scss'

interface FacilityGridProps {
  facilityId: number
  facilityName: string
  accountId: number
}

export const FacilityGrid = ({
  facilityId,
  facilityName,
  accountId
}: FacilityGridProps): JSX.Element => {
  const availableFilters = {
    v: {
      key: 'hasExceedance',
      label: 'Exceedances',
      value: 'hasExceedance',
      type: FilterType.ListSelection,
      options: [
        {
          value: 'Has Exceedance',
          text: 'Has Exceedance'
        },
        {
          value: 'No Exceedance',
          text: 'No Exceedance'
        }
      ]
    }
  }

  return (
    <div className={styles.detailsTableContainer}>
      <ExportButton
        onClick={() =>
          downloadFacilityDetailsExportExcel({ facilityId, facilityName })
        }
      />
      <Responsive platform="desktop">
        <GQLGrid
          query={FACILITY_GRID_QUERY}
          variables={{
            facilityId,
            accountId
          }}
          defaultSorts={{ collectedOn: 'DESC' }}
          client={client}
          availableFilters={availableFilters}
          columns={columns}
          totalGetter={totalGetter}
          searchPlaceholder="Search Fixtures..."
          entityName="Fixture"
          pluralEntityName="Fixtures"
          renderRow={fixture => (
            <AccordionRow
              accountId={accountId}
              fixtureResponse={fixture}
              key={fixture.fixtureId}
              columns={columns}
              platform="desktop"
            />
          )}
        />
      </Responsive>
      <Responsive platform="mobile">
        <GridMemo
          content="Select a fixture from the list below to view its sampling history."
          icon={<Icons.SquareFaucet />}
        />
        <GQLGrid
          query={FACILITY_GRID_QUERY}
          variables={{
            facilityId,
            accountId
          }}
          defaultSorts={{ collectedOn: 'DESC' }}
          client={client}
          availableFilters={availableFilters}
          columns={[column('', 'isExceedance', false), ...mobileColumns]}
          totalGetter={totalGetter}
          searchPlaceholder="Search Fixtures..."
          entityName="Fixture"
          pluralEntityName="Fixtures"
          unstackable
          renderRow={fixture => (
            <AccordionRow
              accountId={accountId}
              fixtureResponse={fixture}
              key={fixture.fixtureId}
              columns={mobileColumns}
              platform="mobile"
            />
          )}
        />
      </Responsive>
    </div>
  )
}

interface ExportButtonProps {
  onClick(): void
}

const ExportButton = ({ onClick }: ExportButtonProps): JSX.Element => (
  <div className={styles.exportButtonWrapper}>
    <Button size="med" onClick={onClick} className={styles.exportButton}>
      <span>
        <Icons.Download />
      </span>
      <span>Export</span>
    </Button>
  </div>
)

const totalGetter = ({ facilityGrid }: any) => facilityGrid.total

export const column = (
  name: string,
  key: string,
  sortable: boolean = false
) => ({
  name,
  key,
  sortable
})

const columns = [
  column('Fixture Code', 'fixtureCode', true),
  column('Fixture Type', 'fixtureType', true),
  column('Fixture Location', 'fixtureLocation', true),
  column('Lead Result', 'leadResult', true),
  column('Remediation Action', 'remediationAction'),
  column('Collection Date', 'collectedOn', true)
]

const mobileColumns = [
  column('Fixture Code', 'fixtureCode', true),
  column('Fixture Type', 'fixtureType', true),
  column('Fixture Location', 'fixtureLocation', true)
]
